import * as React from "react";
import {
    ButtonBarBackground,
    ButtonBarSize,
    ContainerStyle,
    SeparatorSize,
    UIAlignment,
    WireComponent,
    WireScreen,
    WireSectionComponent,
    WireSeparatorComponent,
} from "common/wire";
import { WireBackend } from "webapp/hydrated-ui/types";
import TextField from "webapp/ui/smart/text-field/text-field";
import List from "webapp/ui/smart/list/list";
import ListItem from "webapp/ui/smart/list-item/list-item";
import { ImageSourceKind } from "common/components/portable-renderers";
import { ArrayScreenFormat } from "common/generator/description";
import { assertNever, defined, definedMap, mapFilterUndefined } from "@glideapps/ts-necessities";
import { CardCollection } from "webapp/ui/smart/card-collection/card-collection";
import Hero from "webapp/ui/views/hero/hero";
import { DataGrid } from "webapp/ui/views/data-grid/data-grid";
import { FormSection } from "webapp/ui/views/form-section/form-section";
import UserProfile from "webapp/ui/smart/user-profile/user-profile";
import "twin.macro";
import { ButtonBar } from "webapp/renderers/button-bar";
import { WireComponentKind } from "common/wire/component-kind";
import { WireListContainer } from "webapp/ui/views/wire-list-container/wire-list-container";
import { asMaybeString } from "common/computation-model/data";
import { Fields } from "webapp/ui/views/fields/fields";
import { Breadcrumbs } from "webapp/ui/views/breadcrumbs/breadcrumbs";
import {
    heroStyleToSectionStyle,
    SectionStyle,
    SectionStyleProvider,
    WireContainer,
} from "webapp/ui/views/wire-container/wire-container";
import { Hint } from "webapp/ui/views/wire-hint/wire-hint";
import { WireRenderer } from "./wire-renderer";
import { styled } from "webapp/styled-components";

interface Props {
    readonly currentScreen: WireScreen;
    readonly backend: WireBackend;
}

interface WireComponentRendererProps {
    readonly component: WireComponent;
    readonly backend: WireBackend;
}

const WireComponentRenderer: React.VFC<WireComponentRendererProps> = p => {
    const { component, backend } = p;

    const maybeRunAction = React.useCallback(
        (token: string | undefined, handled: boolean) => {
            if (backend === undefined || token === undefined) return;

            backend.runAction(token, handled);
        },
        [backend]
    );

    if (component.kind === WireComponentKind.Button) {
        return (
            <WireContainer>
                <ButtonBar
                    alignment={UIAlignment.Center}
                    background={ButtonBarBackground.None}
                    size={ButtonBarSize.Wide}
                    title={component.description}
                    buttons={[component]}
                />
            </WireContainer>
        );
    } else if (component.kind === WireComponentKind.TextField) {
        return (
            <WireContainer>
                <TextField
                    placeholder={component.placeholder}
                    dataType="text"
                    isEnabled={true}
                    value={component.value.value}
                    onChange={newVal => backend.valueChanged(component.value.onChangeToken, newVal)}
                    isRequired={false}
                    maximumChars={undefined}
                    title={component.title}
                />
            </WireContainer>
        );
    } else if (component.kind === WireComponentKind.NumberField) {
        return (
            <WireContainer>
                <TextField
                    placeholder={component.placeholder}
                    dataType="number"
                    isEnabled={true}
                    value={asMaybeString(component.value.value) ?? ""}
                    onChange={newVal => backend.valueChanged(component.value.onChangeToken, newVal)}
                    isRequired={false}
                    maximumChars={undefined}
                    title={component.title}
                />
            </WireContainer>
        );
    } else if (component.kind === WireComponentKind.PhoneField) {
        return (
            <WireContainer>
                <TextField
                    placeholder={component.placeholder}
                    dataType="phone"
                    isEnabled={true}
                    value={asMaybeString(component.value.value) ?? ""}
                    onChange={newVal => backend.valueChanged(component.value.onChangeToken, newVal)}
                    isRequired={false}
                    maximumChars={undefined}
                    title={component.title}
                />
            </WireContainer>
        );
    } else if (component.kind === WireComponentKind.EmailField) {
        return (
            <WireContainer>
                <TextField
                    placeholder={component.placeholder}
                    dataType="email"
                    isEnabled={true}
                    value={asMaybeString(component.value.value) ?? ""}
                    onChange={newVal => backend.valueChanged(component.value.onChangeToken, newVal)}
                    isRequired={false}
                    maximumChars={undefined}
                    title={component.title}
                />
            </WireContainer>
        );
    } else if (component.kind === WireComponentKind.List && component.format === ArrayScreenFormat.List) {
        return (
            <WireContainer>
                <WireListContainer
                    title={component.componentTitle}
                    actionTitle={component.componentActionTitle}
                    searchValue={component.searchBar?.value}
                    onSearchChange={definedMap(component.searchBar?.onChangeToken, x => (newVal: string) => {
                        backend.valueChanged(x, newVal);
                    })}
                    onAction={() =>
                        component.componentAction?.token
                            ? backend.runAction(component.componentAction.token, false)
                            : undefined
                    }>
                    <List
                        hasUnevenRows={false}
                        title={component.componentTitle}
                        topAlign={false}
                        items={component.items}>
                        {(item, i) => (
                            <ListItem
                                key={i}
                                appEnvironment={undefined}
                                title={item.title}
                                onClick={() => maybeRunAction(item.action?.token, false)}
                                subtitle={item.subtitle}
                                caption={item.caption}
                                imageSource={{
                                    kind: ImageSourceKind.URLs,
                                    urls: definedMap(item.imageURL, url => [url]) ?? [],
                                }}
                            />
                        )}
                    </List>
                </WireListContainer>
            </WireContainer>
        );
    } else if (component.kind === WireComponentKind.List && component.format === ArrayScreenFormat.CardCollection) {
        return (
            <WireContainer>
                <WireListContainer
                    title={component.componentTitle}
                    actionTitle={component.componentActionTitle}
                    searchValue={component.searchBar?.value}
                    onSearchChange={definedMap(component.searchBar?.onChangeToken, x => (newVal: string) => {
                        backend.valueChanged(x, newVal);
                    })}
                    onAction={() =>
                        component.componentAction?.token
                            ? backend.runAction(component.componentAction.token, false)
                            : undefined
                    }>
                    <CardCollection
                        pageItems={component.items}
                        numPages={component.paging?.numPages ?? 1}
                        page={component.paging?.pageIndex.value ?? 0}
                        describeItem={x => {
                            const {
                                action,
                                secondaryAction,
                                secondaryActionTitle,
                                tertiaryAction,
                                tertiaryActionTitle,
                                ...rest
                            } = x;
                            return {
                                ...rest,
                                onPrimary: definedMap(action?.token, token => () => backend.runAction(token, false)),
                                secondaryAction: secondaryActionTitle,
                                onSecondary: definedMap(
                                    secondaryAction?.token,
                                    token => () => backend.runAction(token, false)
                                ),
                                tertiaryAction: tertiaryActionTitle,
                                onTertiary: definedMap(
                                    tertiaryAction?.token,
                                    token => () => backend.runAction(token, false)
                                ),
                            };
                        }}
                        cardStyle={component.cardStyle}
                        onPageChange={pageIndex => {
                            if (component.paging === undefined) return;
                            backend.valueChanged(component.paging.pageIndex.onChangeToken, pageIndex);
                        }}
                    />
                </WireListContainer>
            </WireContainer>
        );
    } else if (component.kind === WireComponentKind.Hero) {
        const {
            size,
            style,
            withBlur,
            action,
            actionTitle,
            backgroundImage,
            image,
            secondaryAction,
            secondaryActionTitle,
            subtitle,
            title,
            titleEmphasized,
            backgroundEffect,
            imageCorners,
            menuActions,
        } = component;
        return (
            <WireContainer
                backgroundImage={backgroundImage}
                background={heroStyleToSectionStyle(style)}
                blur={withBlur}
                backgroundEffect={backgroundEffect}>
                <Hero
                    size={size}
                    style={style}
                    title={title}
                    backgroundImageURL={backgroundImage}
                    description={subtitle}
                    iconImageURL={image}
                    emphasizedTitle={titleEmphasized}
                    backgroundEffect={backgroundEffect}
                    primaryText={actionTitle}
                    primaryOnClick={definedMap(action?.token, x => () => backend.runAction(x, false))}
                    withBlur={withBlur}
                    secondaryText={secondaryActionTitle}
                    secondaryOnClick={definedMap(secondaryAction?.token, x => () => backend.runAction(x, false))}
                    imageCorners={imageCorners}
                    menuItems={mapFilterUndefined(menuActions ?? [], x =>
                        x.title === undefined || x.action === undefined
                            ? undefined
                            : {
                                  title: x.title,
                                  onClick: () => backend.runAction(defined(x.action?.token), false),
                              }
                    )}
                />
            </WireContainer>
        );
    } else if (component.kind === WireComponentKind.List && component.format === ArrayScreenFormat.DataGrid) {
        return (
            <WireContainer>
                <DataGrid
                    {...component}
                    onPageChange={v =>
                        component.paging?.pageIndex.onChangeToken &&
                        backend.valueChanged(component.paging.pageIndex.onChangeToken, v)
                    }
                />
            </WireContainer>
        );
    } else if (component.kind === WireComponentKind.ContactForm) {
        return (
            <WireContainer>
                <FormSection
                    contactAddress={component.contactAddress}
                    contactEmail={component.contactEmail}
                    contactPhone={component.contactPhone}
                    email={component.email?.value}
                    image={component.image}
                    message={component.message?.value}
                    name={component.name?.value}
                    phone={component.phone?.value}
                    subtitle={component.subtitle}
                    title={component.title}
                    onEmailChange={definedMap(
                        component.email?.onChangeToken,
                        token => x => backend.valueChanged(token, x)
                    )}
                    onMessageChange={definedMap(
                        component.message?.onChangeToken,
                        token => x => backend.valueChanged(token, x)
                    )}
                    onNameChange={definedMap(
                        component.name?.onChangeToken,
                        token => x => backend.valueChanged(token, x)
                    )}
                    onPhoneChange={definedMap(
                        component.phone?.onChangeToken,
                        token => x => backend.valueChanged(token, x)
                    )}
                    onSubmit={definedMap(component.submitAction.token, token => () => backend.runAction(token, false))}
                />
            </WireContainer>
        );
    } else if (component.kind === WireComponentKind.UserProfile) {
        return (
            <WireContainer>
                <UserProfile title={component.title} subtitle={component.subtitle} imageURL={component.image} />
            </WireContainer>
        );
    } else if (component.kind === WireComponentKind.DynamicButtonBar) {
        return (
            <WireContainer>
                <ButtonBar {...component} />
            </WireContainer>
        );
    } else if (component.kind === WireComponentKind.Breadcrumbs) {
        return (
            <WireContainer>
                <Breadcrumbs {...component} backend={backend} />
            </WireContainer>
        );
    } else if (component.kind === WireComponentKind.Container) {
        return <Container {...component} backend={backend} />;
    } else if (component.kind === WireComponentKind.Fields) {
        return (
            <WireContainer>
                <Fields {...component} backend={backend} />
            </WireContainer>
        );
    } else if (component.kind === WireComponentKind.Hint) {
        return (
            <WireContainer>
                <Hint {...component} backend={backend} />
            </WireContainer>
        );
    } else if (component.kind === WireComponentKind.Separator) {
        return (
            <WireContainer>
                <Separator {...component} backend={backend} />
            </WireContainer>
        );
    } else {
        return assertNever(component);
    }
};

const Separator: WireRenderer<WireSeparatorComponent> = p => {
    const { drawLine, size } = p;
    let s = 0;
    if (size === SeparatorSize.Medium) {
        s = 8;
    } else if (size === SeparatorSize.Large) {
        s = 24;
    }

    return <div tw="bg-border-dark" style={{ marginTop: s, marginBottom: s, height: drawLine ? 1 : 0 }} />;
};

const ContainerSectionStyle = styled.section`
    .full-bleed + &.full-bleed {
        margin-top: 0;
    }
`;

// TODO: This doesn't belong in here, but there's a dependency cycle between
// this and `WireComponentRenderer`
export const Container: WireRenderer<WireSectionComponent> = props => {
    const { containerStyle } = props;
    const contents = (
        <div>
            {props.components.map((c, i) => (
                <WireComponentRenderer key={i} component={c} backend={props.backend} />
            ))}
        </div>
    );
    if (containerStyle === ContainerStyle.Default) return <ContainerSectionStyle>{contents}</ContainerSectionStyle>;
    if (containerStyle === ContainerStyle.Accent)
        return (
            <SectionStyleProvider value={SectionStyle.Accent}>
                <ContainerSectionStyle className="accent-bg full-bleed" tw="py-6 not-first:mt-6">
                    {contents}
                </ContainerSectionStyle>
            </SectionStyleProvider>
        );
    return (
        <ContainerSectionStyle className="full-bleed" tw="py-6 bg-n0 not-first:mt-6">
            {contents}
        </ContainerSectionStyle>
    );
};

export const WireApp: React.VFC<Props> = p => {
    const { currentScreen, backend } = p;

    const content = React.useMemo(() => {
        const items = currentScreen?.components ?? [];
        return items.map((i, index) => {
            if (i === null) return undefined;
            return <WireComponentRenderer key={index} component={i} backend={backend} />;
        });
    }, [backend, currentScreen?.components]);

    return <>{content}</>;
};
